/**
 * The three-axis taste profile the onboarding duels produce.
 *
 * `base`  — animal protein at the centre of the plate, or plant-forward.
 * `time`  — a weeknight budget of ~30 min, or "the time it takes".
 * `heat`  — bold, spiced and grilled, or mild, buttery and gentle.
 *
 * The profile is deliberately tiny: three binary axes give eight cells, each
 * with a name for the team (see spec.html › "The eight profiles") and a
 * "Concrètement" line the cook reads. Everything that turns the axes into
 * generator inputs lives here so the onboarding week, the planner's
 * "Régénérer" and the recipes screen all read the same projection.
 */
import type { MealFrequencyPreferences, MealSchedulePreferences } from '../redux/userPreferencesSlice';
import { classifyDietKind } from '../utils/dietClassifier';
import { busyEveningMinutes } from './planRanking';

export interface TasteAxes {
  base: 'protein' | 'vegetal';
  time: 'quick' | 'long';
  heat: 'bold' | 'mild';
}

export type Axis = keyof TasteAxes;

export const AXES: Axis[] = ['base', 'time', 'heat'];

export type ProfileKey =
  | 'protein-quick-bold' | 'protein-quick-mild' | 'protein-long-bold' | 'protein-long-mild'
  | 'vegetal-quick-bold' | 'vegetal-quick-mild' | 'vegetal-long-bold' | 'vegetal-long-mild';

/** Answer `a` is always the protein / quick / bold side of a pair. */
export function profileFromAnswers(answers: ReadonlyArray<'a' | 'b'>): TasteAxes | null {
  if (answers.length < 3) return null;
  return {
    base: answers[0] === 'a' ? 'protein' : 'vegetal',
    time: answers[1] === 'a' ? 'quick' : 'long',
    heat: answers[2] === 'a' ? 'bold' : 'mild',
  };
}

export function profileKey(axes: TasteAxes): ProfileKey {
  return `${axes.base}-${axes.time}-${axes.heat}` as ProfileKey;
}

export function flipAxis(axes: TasteAxes, axis: Axis): TasteAxes {
  switch (axis) {
    case 'base': return { ...axes, base: axes.base === 'protein' ? 'vegetal' : 'protein' };
    case 'time': return { ...axes, time: axes.time === 'quick' ? 'long' : 'quick' };
    case 'heat': return { ...axes, heat: axes.heat === 'bold' ? 'mild' : 'bold' };
  }
}

export function oppositeAxes(axes: TasteAxes): TasteAxes {
  return flipAxis(flipAxis(flipAxis(axes, 'base'), 'time'), 'heat');
}

// ---------------------------------------------------------------------------
// Intensity — the one factor no catalog field carries. Keyword banks, FR + EN,
// matched on the title and the ingredient lines. Shared with the deck tooling.
// ---------------------------------------------------------------------------

export const BOLD_KEYWORDS = [
  'épic', 'epic', 'spicy', 'piment', 'chili', 'chile', 'harissa', 'sriracha', 'gochujang', 'cari', 'curry', 'tandoori', 'tikka',
  'jerk', 'cajun', 'chimichurri', 'grill', 'bbq', 'barbecue', 'kimchi', 'jalape', 'chipotle', 'fumé', 'sambal', 'thaï', 'thai',
  'sichuan', 'szechuan', 'citronnelle', 'lemongrass', 'wok', 'sauté', 'stir-fry', 'stir fry', 'tacos', 'fajita', 'shawarma', 'kebab',
  'brochette', 'satay', 'teriyaki', 'gingembre', 'ginger', 'lime', 'coriandre', 'cilantro', 'cumin', 'paprika', 'ras el hanout', "za'atar", 'za’atar', 'zaatar',
  'poivre noir', 'wasabi', 'miso', 'bibimbap', 'pad thaï', 'pad thai', 'vindaloo', 'masala', 'chorizo', 'merguez', 'tex-mex', 'mexicain',
];

export const MILD_KEYWORDS = [
  'doux', 'crème', 'creme', 'crémeu', 'cream', 'gratin', 'beurre', 'butter', 'citron', 'lemon', 'purée', 'mijoté', 'réconfort',
  'soupe', 'potage', 'soup', 'risotto', 'lait', 'fromage', 'cheese', 'macaroni', 'béchamel', 'quiche', 'omelette', 'rôti', 'roast',
  'pommes de terre', 'potato', 'pâtes', 'pasta', 'nouilles au beurre', 'pâté chinois', 'shepherd', 'pot-au-feu', 'blanquette', 'poché',
  'vapeur', 'steamed', 'herbes', 'herbs', 'persil', 'parsley', 'aneth', 'dill', 'miel', 'honey', 'érable', 'maple', 'carbonara',
  'alfredo', 'chowder', 'velouté', 'lasagne', 'parmigiana', 'bourguignon', 'ragoût', 'stew', 'boulettes', 'meatball',
];

export interface IntensityRecipe {
  title: string;
  ingredients?: string[];
}

function countHits(text: string, bank: string[]): number {
  let n = 0;
  for (const k of bank) if (text.includes(k)) n++;
  return n;
}

/**
 * Title hits count double: "Sauté de tofu" is bold even if the ingredient
 * list mentions butter once. Ties and silence are `neutral`.
 */
const intensityCache = new WeakMap<IntensityRecipe, 'bold' | 'mild' | 'neutral'>();

export function classifyIntensity(recipe: IntensityRecipe): 'bold' | 'mild' | 'neutral' {
  // Once per recipe object: the planner scores every recipe on every slot,
  // and these two keyword banks were a quarter of its run on the catalogue.
  const cached = intensityCache.get(recipe);
  if (cached) return cached;
  const verdict = intensityOf(recipe);
  intensityCache.set(recipe, verdict);
  return verdict;
}

function intensityOf(recipe: IntensityRecipe): 'bold' | 'mild' | 'neutral' {
  const title = recipe.title.toLowerCase();
  const ing = (recipe.ingredients ?? []).join(' ').toLowerCase();
  const bold = countHits(title, BOLD_KEYWORDS) * 2 + countHits(ing, BOLD_KEYWORDS);
  const mild = countHits(title, MILD_KEYWORDS) * 2 + countHits(ing, MILD_KEYWORDS);
  if (bold === mild) return 'neutral';
  return bold > mild ? 'bold' : 'mild';
}

// ---------------------------------------------------------------------------
// Projection onto the generators
// ---------------------------------------------------------------------------

export interface BonusRecipe extends IntensityRecipe {
  ingredients: string[];
  readyInMinutes: number;
  handsOnMinutes?: number;
  diets?: string[];
}

/**
 * Additive bonus on the generator's base score of 100. Strong enough that a
 * flipped chip visibly changes the week (the flip test asserts ≥ 3 of 7).
 */
export function tasteAxesBonus(recipe: BonusRecipe, axes: TasteAxes, isWeekend: boolean): number {
  // The dinner generator picks at random among the best 15+ candidates
  // (weightedRandomPick), so a soft preference has to move a recipe by more
  // than its 150-point window to be felt, and a firm one has to sink it below
  // the -500 floor that removes it from the pool. Hence the sizes below.
  let bonus = 0;

  const heat = classifyIntensity(recipe);
  // Opposite heat is disqualifying (below the generator's -500 floor): a cook
  // who asked for "rien qui pique" must not meet a chili on Tuesday.
  if (heat !== 'neutral') bonus += heat === axes.heat ? 80 : -1000;

  const kind = classifyDietKind(recipe);
  if (axes.base === 'vegetal') bonus += kind === 'veg' ? 60 : kind === 'meat' ? -40 : 0;
  else bonus += kind === 'veg' ? -30 : 30;

  const minutes = recipe.readyInMinutes || 0;
  if (axes.time === 'quick') {
    if (!isWeekend) {
      // A weeknight over 45 min is the one thing a "quick" cook asked not to get.
      const evening = busyEveningMinutes(recipe);
      if (evening > 0 && evening <= 30) bonus += 40;
      else if (evening > 45) bonus -= 1000; // disqualifying, same reason as heat
      else if (evening > 30) bonus -= 40;
    }
  } else if (isWeekend) {
    if (minutes >= 45) bonus += 40;
    else if (minutes > 0 && minutes <= 20) bonus -= 30;
  }

  return bonus;
}

/** Soft weekly distribution for the planner's `buildGenerationPlan`. */
export function axesToTargets(axes: TasteAxes): { fish: number; veg: number; meat: number } {
  return axes.base === 'vegetal' ? { veg: 4, fish: 1, meat: 2 } : { veg: 1, fish: 2, meat: 4 };
}

/**
 * Schedule and frequency inputs for `generateDinnerOnlyPlan`. The cook's own
 * schedule is the base; only the time caps and the veg/fish counts move.
 */
export function axesToDinnerConfig(
  axes: TasteAxes,
  base: MealSchedulePreferences,
): { mealSchedule: MealSchedulePreferences; mealFrequencyPreferences: MealFrequencyPreferences } {
  const days: MealSchedulePreferences['days'] = { ...base.days };
  for (let d = 0; d <= 6; d++) {
    const pref = days[d];
    if (!pref) continue;
    const weekend = d === 0 || d === 6;
    const cap = axes.time === 'quick' && !weekend ? 30 : null;
    days[d] = { ...pref, maxCookTimeMinutes: cap };
  }
  const mealFrequencyPreferences: MealFrequencyPreferences =
    axes.base === 'vegetal' ? { vegetarianPerWeek: 4, fishPerWeek: 1 } : { vegetarianPerWeek: 1, fishPerWeek: 2 };
  return { mealSchedule: { ...base, days }, mealFrequencyPreferences };
}

/**
 * "Poulet au beurre express, sauce…" → "poulet au beurre express". Only the
 * first letter drops its capital so the name reads mid-sentence; the rest
 * stays as written, because "de Josée di Stasio" is a name, not a shout.
 */
export function plateName(title: string): string {
  const head = title.split(',')[0].trim();
  return head.charAt(0).toLowerCase() + head.slice(1);
}
